import {
  AptosProvider,
  camelCaseKey,
  deserialize,
  MoveValueType,
  StructValueType,
} from '@aries-markets/create-sdk';
import { compact, isObject, isString } from 'lodash';
import { TypeInfo } from '../idl/dependencies/aptos_framework/type_info';
import { Pair } from '../idl/dependencies/UtilTypes/pair';
import { delay } from './util';

export const ARIES_TEST_PROGRAM_ADDRESS =
  '0xeef200f2a06957a1548685c0feec9f1a04db27598fadab7f6daf30428fd064d3';

export const ARIES_PROGRAM_ADDRESS =
  '0x9770fa9c725cbd97eb50b2be5f7416efdfd1f1554beb0750d4dae4c64e860da3';
// TODO: we should utilise schema info for serialisation instead of intristics
const serializeTableKey = <K extends string | StructValueType>(
  keyType: K,
  keyTypeAddress: string = '0x1',
) => {
  const serializeTypeInfo = (typeInfo: TypeInfo) => {
    return {
      account_address: typeInfo.account_address,
      // reserve
      module_name: toHex(typeInfo.module_name),
      // `ReserveCoinContainer<0x1::aptos_coin::AptosCoin>`
      struct_name: toHex(typeInfo.struct_name),
    };
  };

  if (keyType === 'TypeInfo') {
    return (typeInfo: TypeInfo) => ({
      key: serializeTypeInfo(typeInfo),
      key_type: '0x1::type_info::TypeInfo',
    });
  }

  if (isObject(keyType) && keyType.structName === 'Pair') {
    let pairTypeProgram = keyTypeAddress;
    if (
      ![ARIES_TEST_PROGRAM_ADDRESS, ARIES_PROGRAM_ADDRESS].includes(
        keyTypeAddress,
      )
    ) {
      pairTypeProgram = ARIES_PROGRAM_ADDRESS;
    }

    return (pairInfo: Pair<TypeInfo, TypeInfo>) => {
      const fst = serializeTypeInfo(pairInfo.fst);
      const snd = serializeTypeInfo(pairInfo.snd);
      return {
        key: { fst, snd },
        key_type: `${pairTypeProgram}::pair::Pair<0x1::type_info::TypeInfo, 0x1::type_info::TypeInfo>`,
      };
    };
  }

  throw Error(`unsupported type: ${keyType}`);
};

// Define inner custom runtime deserilizer here
export const getTableResolver = (
  value: { handle: string; length: string },
  ctx: {
    path?: string;
    schema: MoveValueType;
    provider: AptosProvider;
    program: string;
  },
) => {
  if (isString(ctx.schema)) {
    return value;
  }
  const { typeArgs: [keyType, valueType] = [] } = ctx.schema;
  if (!isObject(valueType)) {
    return value;
  }

  const tableValueType = `${ctx.program}::${valueType.module}::${valueType.structName}`;
  const buildParams = serializeTableKey(keyType, ctx.program);

  const MAX_FETCH_ATTEMPTS = 3;

  const fromKey = async (
    keyValue: Parameters<typeof buildParams>[0],
    options?: { version?: number | bigint },
  ) => {
    const params = {
      // @ts-ignore-next-line
      ...buildParams(keyValue),
      value_type: tableValueType,
    };

    /* eslint-disable no-await-in-loop */
    for (let attempt = 1; ; attempt++) {
      try {
        const res = await ctx.provider.client.getTableItem(
          value.handle,
          params,
          {
            ledgerVersion: options?.version,
          },
        );

        const parsed = await deserialize(res, {
          ...ctx,
          schema: (valueType as StructValueType).schema,
        });

        return camelCaseKey(parsed);
      } catch (err) {
        // A 404 means the item genuinely doesn't exist; anything else
        // (rate limits, transient node errors) is worth retrying so the
        // caller doesn't silently lose table entries.
        const status = (err as { status?: number } | undefined)?.status;
        if (status === 404 || attempt >= MAX_FETCH_ATTEMPTS) {
          // eslint-disable-next-line no-console
          console.error(err);
          return null;
        }

        await delay(500 * attempt + Math.random() * 250);
      }
    }
    /* eslint-enable no-await-in-loop */
  };

  const fromKeys = async (
    keys: Array<Parameters<typeof buildParams>[0]>,
    options?: { version?: number | bigint; noDelay?: boolean },
  ) => {
    const arr = await Promise.all(
      keys.map(async (key, index) => {
        // eslint-disable-next-line no-promise-executor-return
        if (!options?.noDelay) {
          await delay(index * 15);
        }

        const res = await fromKey({ ...key }, options);
        if (!res) {
          return null;
        }

        return {
          key,
          value: res,
        };
      }),
    );

    return compact(arr);
  };

  return {
    fromKey,
    fromKeys,
    length: Number(value.length),
    handle: value.handle,
  };
};

const toHex = (str: string) => Buffer.from(str, 'utf-8').toString('hex');
